/* eslint-disable no-script-url,jsx-a11y/anchor-is-valid */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import { CircularProgress } from "@mui/material";
import moment from "moment";
import SnackBarTool from "../../../../../../_metronic/_partials/widgets/snack-bar/SnackBar";
import {
  closeFranchiseDebt,
  createFranchiseDebt,
  deleteFranchiseDebt,
  getFranchiseDebt,
  pauseFranchiseDebt,
  resumeFranchiseDebt,
  updateFranchiseDebt,
} from "../../../_redux/franchises/franchiseDebtCrud";
import {
  apiDebtStatusToSlug,
  DebtStatusBadge,
  DEBT_STATUS_BADGE_BASE_CLASS,
} from "../DebtStatusBadge";
import { DebtCollectionSectionDrawer } from "../debt-collection-canva/DebtCollectionSectionDrawer";
import { DebtCollectionCloseAccountModal } from "../debt-collection-dialog/DebtCollectionCloseAccountModal";
import { DebtCollectionDeleteModal } from "../debt-collection-dialog/DebtCollectionDeleteModal";
import { DebtCollectionSectionScopedCss } from "./DebtCollectionSectionScopedCss";
import {
  CARD_BORDER,
  CURRENT_CONFIG_TITLE_STYLE,
  DEBT_CARD_TITLE_FONT_SIZE,
  DEBT_SUPER_ADMIN_BADGE,
  DEBT_TITLE_STYLE,
  MECHANISM_FULL,
  MECHANISM_PERCENT,
  NAVY,
} from "./DebtCollectionSectionConstants";
import {
  formatDebtUserRef,
  formatMoney,
  getDebtStatusOwnerDisplay,
  parseDebtRecordFromFetchResponse,
} from "./DebtCollectionSectionHelpers";
import {
  EmptyStateCardsIcon,
  InfoPanel,
  SummaryTile,
} from "./DebtCollectionSectionUi";
import {
  canAccessDebtCollectionFlow,
  isDebtCollectionSuperAdmin,
} from "../debtCollectionRoles";
import { getApiErrorMessage } from "../../../../../utils/commonFunction";

/** Default empty form state for create/edit debt drawer. */
function createEmptyDebtForm() {
  return {
    collection_mechanism: MECHANISM_PERCENT,
    collection_percent: "",
    start_date: null,
    unique_reference: "",
    opening_balance: "",
    current_balance: "",
  };
}

/** Parses API start date string into a Date or null. */
function parseDebtRecordStartDate(raw) {
  if (!raw) return null;
  const m = moment(raw);
  return m.isValid() ? m.toDate() : null;
}

/** Maps an API debt record into drawer form fields. */
function recordToDebtForm(record) {
  return {
    collection_mechanism:
      Number(record.collection_mechanism) || MECHANISM_PERCENT,
    collection_percent:
      record.collection_percent != null && record.collection_percent !== ""
        ? String(record.collection_percent)
        : "",
    start_date: parseDebtRecordStartDate(record.start_date),
    unique_reference:
      record.unique_reference != null ? String(record.unique_reference) : "",
    opening_balance:
      record.opening_balance != null ? String(record.opening_balance) : "",
    current_balance:
      record.current_balance != null ? String(record.current_balance) : "",
  };
}

/** Short summary label for mechanism + optional percent (summary tiles). */
function debtMechanismLabelShort(m, p) {
  return Number(m) === MECHANISM_FULL
    ? "100% collection"
    : "% based" + (p ? ` (${p}%)` : "");
}

const DEBT_PRIMARY_BTN_STYLE = Object.freeze({
  backgroundColor: NAVY,
  borderColor: NAVY,
  minWidth: "88px",
});

const DEBT_BADGE_SUPER_STYLE = Object.freeze({
  fontWeight: 600,
  letterSpacing: "0.06em",
  color: DEBT_SUPER_ADMIN_BADGE.color,
  backgroundColor: DEBT_SUPER_ADMIN_BADGE.backgroundColor,
  borderColor: DEBT_SUPER_ADMIN_BADGE.color,
  borderWidth: 1,
  borderStyle: "solid",
});

const DEBT_BADGE_VIEW_STYLE = Object.freeze({
  fontWeight: 600,
  letterSpacing: "0.06em",
  color: "#4b5563",
  backgroundColor: "#f3f4f6",
  borderColor: "#d1d5db",
  borderWidth: 1,
  borderStyle: "solid",
});

/** Initial state factory for the close-account modal form. */
const INITIAL_CLOSE_FORM = () => ({
  closure_date: new Date(),
  remark: "",
});

/** Renders franchise debt configuration card, drawer, and modals for one franchise. */
function DebtCollectionSectionContent({ franchiseId, user }) {
  const roles = user?.roles;
  const canManageDebt = useMemo(() => isDebtCollectionSuperAdmin(roles), [
    roles,
  ]);
  const isSuperAdmin = canManageDebt;

  const closedByDisplay = useMemo(() => {
    if (!user) return "";
    return (
      user.fullname ||
      user.full_name ||
      [user.first_name, user.last_name].filter(Boolean).join(" ") ||
      user.name ||
      user.email ||
      ""
    );
  }, [user]);

  const closeAccountClosedByField = useMemo(() => {
    if (closedByDisplay) return `${closedByDisplay} (read only)`;
    return "Current super admin (read only)";
  }, [closedByDisplay]);

  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [snack, setSnack] = useState({ call: false, type: "", message: "" });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [submitting, setSubmitting] = useState(false);

  const [deleteModal, setDeleteModal] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [closeSubmitting, setCloseSubmitting] = useState(false);
  const [closeRemarkError, setCloseRemarkError] = useState("");
  const [pauseSubmitting, setPauseSubmitting] = useState(false);
  const [resumeSubmitting, setResumeSubmitting] = useState(false);

  const [formErrors, setFormErrors] = useState({});
  const [form, setForm] = useState(() => createEmptyDebtForm());

  const [closeForm, setCloseForm] = useState(INITIAL_CLOSE_FORM);

  const debtFetchGeneration = useRef(0);

  const loadDebt = useCallback(() => {
    if (!franchiseId) return;
    const gen = ++debtFetchGeneration.current;
    setLoading(true);
    getFranchiseDebt(franchiseId)
      .then((res) => {
        if (debtFetchGeneration.current !== gen) return;
        setRecord(parseDebtRecordFromFetchResponse(res));
      })
      .catch(() => {
        if (debtFetchGeneration.current !== gen) return;
        setRecord(null);
        setSnack({
          call: true,
          type: "error",
          message: "Unable to load debt collection configuration.",
        });
      })
      .finally(() => {
        if (debtFetchGeneration.current !== gen) return;
        setLoading(false);
      });
  }, [franchiseId]);

  useEffect(() => {
    loadDebt();
  }, [loadDebt]);

  const status = useMemo(
    () => record?._statusSlug ?? apiDebtStatusToSlug(record?.status),
    [record],
  );

  const headerFlags = useMemo(
    () => ({
      readOnly: status === "closed",
      showHeaderEditDelete: status === "pending",
      showHeaderPause: status === "ongoing",
      showHeaderResume: status === "paused",
      showHeaderCloseAccount: status === "ongoing" || status === "paused",
      showHeaderAddNew: status === "completed" || status === "closed",
    }),
    [status],
  );

  const badgeStyle = isSuperAdmin
    ? DEBT_BADGE_SUPER_STYLE
    : DEBT_BADGE_VIEW_STYLE;

  const openCreate = useCallback(() => {
    if (!canManageDebt) return;
    setDrawerMode("create");
    setFormErrors({});
    setForm(createEmptyDebtForm());
    setDrawerOpen(true);
  }, [canManageDebt]);

  const openEdit = useCallback(() => {
    if (!record || !canManageDebt) return;
    setDrawerMode("edit");
    setFormErrors({});
    setForm(recordToDebtForm(record));
    setDrawerOpen(true);
  }, [record, canManageDebt]);

  const openCloseAccountModal = useCallback(() => {
    setCloseForm({
      closure_date: new Date(),
      remark: "",
    });
    setCloseRemarkError("");
    setCloseModal(true);
  }, []);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const validateForm = useCallback(() => {
    const err = {};
    if (!form.start_date) {
      err.start_date = "Started at is required.";
    } else {
      const picked = moment(form.start_date).startOf("day");
      const today = moment().startOf("day");
      if (picked.isBefore(today)) {
        err.start_date = "Started at must be today or a future date.";
      }
    }
    if (!String(form.unique_reference || "").trim()) {
      err.unique_reference = "Unique reference is required.";
    }
    const ob = Number(String(form.opening_balance).replace(/,/g, ""));
    if (form.opening_balance === "" || Number.isNaN(ob) || ob <= 0) {
      err.opening_balance = "Enter a positive opening balance.";
    }
    if (form.collection_mechanism === MECHANISM_PERCENT) {
      const p = Number(form.collection_percent);
      if (
        form.collection_percent === "" ||
        Number.isNaN(p) ||
        p < 1 ||
        p > 100
      ) {
        err.collection_percent = "Enter a valid percentage (1–100).";
      }
    }
    setFormErrors(err);
    return Object.keys(err).length === 0;
  }, [form]);

  const dismissFieldErrorIfValid = useCallback((fieldKey, nextForm) => {
    let stillInvalid = false;
    if (fieldKey === "start_date") {
      if (!nextForm.start_date) stillInvalid = true;
      else {
        const picked = moment(nextForm.start_date).startOf("day");
        const today = moment().startOf("day");
        if (picked.isBefore(today)) stillInvalid = true;
      }
    } else if (fieldKey === "opening_balance") {
      const ob = Number(String(nextForm.opening_balance).replace(/,/g, ""));
      if (nextForm.opening_balance === "" || Number.isNaN(ob) || ob <= 0) {
        stillInvalid = true;
      }
    } else if (fieldKey === "collection_percent") {
      if (nextForm.collection_mechanism === MECHANISM_PERCENT) {
        const p = Number(nextForm.collection_percent);
        if (
          nextForm.collection_percent === "" ||
          Number.isNaN(p) ||
          p < 1 ||
          p > 100
        ) {
          stillInvalid = true;
        }
      }
    }
    if (!stillInvalid) {
      setFormErrors((prev) => {
        if (!prev[fieldKey]) return prev;
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
    }
  }, []);

  const buildFormData = useCallback(() => {
    const fd = new FormData();
    fd.append("franchise_id", String(franchiseId));
    fd.append("collection_mechanism", String(form.collection_mechanism));
    if (form.collection_mechanism === MECHANISM_PERCENT) {
      fd.append("collection_percent", String(form.collection_percent).trim());
    }
    const ob = String(form.opening_balance)
      .replace(/,/g, "")
      .trim();
    fd.append("opening_balance", ob);
    fd.append("unique_reference", String(form.unique_reference).trim());
    if (form.start_date) {
      fd.append("started_at", moment(form.start_date).format("YYYY-MM-DD"));
    }
    return fd;
  }, [form, franchiseId]);

  const handleSubmitForm = useCallback(
    (e) => {
      e.preventDefault();
      const isUpdate = drawerMode === "edit" && record?.id;
      if (!canManageDebt) return;
      if (!validateForm()) return;
      setSubmitting(true);
      const req = isUpdate
        ? updateFranchiseDebt(record.id, buildFormData())
        : createFranchiseDebt(buildFormData());
      req
        .then((res) => {
          setSnack({
            call: true,
            type: "success",
            message: res?.data?.message || "Saved successfully.",
          });
          setDrawerOpen(false);
          loadDebt();
        })
        .catch((error) => {
          setSnack({
            call: true,
            type: "error",
            message: getApiErrorMessage(error, "Save failed."),
          });
          if (error?.response?.data?.errors?.unique_reference) {
            setFormErrors((prev) => ({
              ...prev,
              unique_reference: String(
                error?.response?.data?.errors?.unique_reference,
              ),
            }));
          }
        })
        .finally(() => setSubmitting(false));
    },
    [drawerMode, record, canManageDebt, validateForm, buildFormData, loadDebt],
  );

  const handleDelete = useCallback(() => {
    if (!record?.id || !canManageDebt) return;
    setSubmitting(true);
    deleteFranchiseDebt(record.id)
      .then((res) => {
        setSnack({
          call: true,
          type: "success",
          message: res?.data?.message || "Deleted.",
        });
        setDeleteModal(false);
        setRecord(null);
        loadDebt();
      })
      .catch((error) => {
        setSnack({
          call: true,
          type: "error",
          message: getApiErrorMessage(error, "Delete failed."),
        });
      })
      .finally(() => setSubmitting(false));
  }, [record, canManageDebt, loadDebt]);

  const handleCloseAccount = useCallback(() => {
    if (!record?.id || !canManageDebt) return;
    if (!closeForm.remark.trim()) {
      setCloseRemarkError("A reason is required for premature closure.");
      return;
    }
    setCloseRemarkError("");
    setCloseSubmitting(true);
    const fd = new FormData();
    fd.append("closed_reason", closeForm.remark.trim());
    fd.append("closed_at", moment(closeForm.closure_date).format("YYYY-MM-DD"));

    closeFranchiseDebt(record.id, fd)
      .then((res) => {
        setSnack({
          call: true,
          type: "success",
          message: res?.data?.message || "Account closed.",
        });
        setCloseModal(false);
        loadDebt();
      })
      .catch((error) => {
        setSnack({
          call: true,
          type: "error",
          message: getApiErrorMessage(error, "Close account failed."),
        });
      })
      .finally(() => setCloseSubmitting(false));
  }, [record, canManageDebt, closeForm, loadDebt]);

  const handlePause = useCallback(() => {
    if (!record?.id || !canManageDebt) return;
    setPauseSubmitting(true);
    pauseFranchiseDebt(record.id)
      .then((res) => {
        setSnack({
          call: true,
          type: "success",
          message: res?.data?.message || "Configuration paused.",
        });
        loadDebt();
      })
      .catch((error) => {
        setSnack({
          call: true,
          type: "error",
          message: getApiErrorMessage(error, "Pause failed."),
        });
      })
      .finally(() => setPauseSubmitting(false));
  }, [record, canManageDebt, loadDebt]);

  const handleResume = useCallback(() => {
    if (!record?.id || !canManageDebt) return;
    setResumeSubmitting(true);
    resumeFranchiseDebt(record.id)
      .then((res) => {
        setSnack({
          call: true,
          type: "success",
          message: res?.data?.message || "Configuration resumed.",
        });
        loadDebt();
      })
      .catch((error) => {
        setSnack({
          call: true,
          type: "error",
          message: getApiErrorMessage(error, "Resume failed."),
        });
      })
      .finally(() => setResumeSubmitting(false));
  }, [record, canManageDebt, loadDebt]);

  const openDeleteModal = useCallback(() => setDeleteModal(true), []);
  const closeDeleteModal = useCallback(() => setDeleteModal(false), []);
  const closeCloseModal = useCallback(() => setCloseModal(false), []);

  const {
    readOnly,
    showHeaderEditDelete,
    showHeaderPause,
    showHeaderResume,
    showHeaderCloseAccount,
    showHeaderAddNew,
  } = headerFlags;

  return (
    <>
      <DebtCollectionSectionScopedCss />
      <div
        id="debt-collection-section"
        className="col-lg-12 col-xl-12 mb-6"
        style={{ marginBottom: "24px" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
          <h2 className="mb-0 pr-3" style={DEBT_TITLE_STYLE}>
            Debt Collection Configuration
          </h2>
          <span
            className={`${DEBT_STATUS_BADGE_BASE_CLASS} mt-2 mt-md-0`}
            style={badgeStyle}
          >
            {isSuperAdmin ? "Super admin actions" : "View only"}
          </span>
        </div>

        <div
          className="card card-custom gutter-b mb-10 border bg-white rounded-lg"
          style={{ borderColor: CARD_BORDER }}
        >
          <div className="card-body bg-white" style={{ padding: "24px 20px" }}>
            {loading ? (
              <div className="text-center py-10">
                <CircularProgress />
              </div>
            ) : !record ? (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
                  <span
                    className="font-weight-bolder"
                    style={CURRENT_CONFIG_TITLE_STYLE}
                  >
                    Current configuration
                  </span>
                  {canManageDebt ? (
                    <Button
                      variant="dark"
                      size="sm"
                      className="rounded mt-2 mt-sm-0"
                      style={DEBT_PRIMARY_BTN_STYLE}
                      onClick={openCreate}
                    >
                      Add new
                    </Button>
                  ) : null}
                </div>
                <div
                  className="text-center py-8 px-4 rounded-lg border bg-white"
                  style={{
                    borderColor: CARD_BORDER,
                  }}
                >
                  <EmptyStateCardsIcon />
                  <h5
                    className="font-weight-bold mt-4 mb-2"
                    style={{ color: NAVY, fontSize: DEBT_CARD_TITLE_FONT_SIZE }}
                  >
                    No debt collection configuration found
                  </h5>
                  {canManageDebt ? (
                    <>
                      <p
                        className="text-muted mb-4 mx-auto small"
                        style={{ maxWidth: "420px" }}
                      >
                        Create the first configuration to start collecting
                        against the balance.
                      </p>
                      <Button
                        variant="dark"
                        size="sm"
                        className="rounded mt-2 mt-sm-0"
                        style={DEBT_PRIMARY_BTN_STYLE}
                        onClick={openCreate}
                      >
                        Create configuration
                      </Button>
                    </>
                  ) : null}
                </div>
              </>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
                  <div className="d-flex align-items-center flex-wrap">
                    <span
                      className="font-weight-bolder mr-3 mb-2 mb-sm-0"
                      style={CURRENT_CONFIG_TITLE_STYLE}
                    >
                      Current configuration
                    </span>
                    <DebtStatusBadge status={status} />
                  </div>
                  <div
                    className="d-flex flex-wrap align-items-center mt-2 mt-md-0"
                    style={{ gap: "8px" }}
                  >
                    {showHeaderEditDelete && canManageDebt ? (
                      <>
                        <Button
                          variant="dark"
                          size="sm"
                          className="rounded mb-2 mb-md-0"
                          style={DEBT_PRIMARY_BTN_STYLE}
                          onClick={openEdit}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="rounded mb-2 mb-md-0"
                          onClick={openDeleteModal}
                        >
                          Delete
                        </Button>
                      </>
                    ) : null}
                    {showHeaderPause && canManageDebt ? (
                      <Button
                        variant="outline-warning"
                        size="sm"
                        className="rounded mb-2 mb-md-0"
                        disabled={pauseSubmitting}
                        onClick={handlePause}
                      >
                        {pauseSubmitting ? "Pausing…" : "Pause"}
                      </Button>
                    ) : null}
                    {showHeaderResume && canManageDebt ? (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="rounded mb-2 mb-md-0"
                        disabled={resumeSubmitting}
                        onClick={handleResume}
                      >
                        {resumeSubmitting ? "Resuming…" : "Resume"}
                      </Button>
                    ) : null}
                    {showHeaderCloseAccount && canManageDebt ? (
                      <Button
                        variant="dark"
                        size="sm"
                        className="rounded mb-2 mb-md-0"
                        style={DEBT_PRIMARY_BTN_STYLE}
                        onClick={openCloseAccountModal}
                      >
                        Close account
                      </Button>
                    ) : null}
                    {showHeaderAddNew && canManageDebt ? (
                      <Button
                        variant="dark"
                        size="sm"
                        className="rounded mb-2 mb-md-0"
                        style={DEBT_PRIMARY_BTN_STYLE}
                        onClick={openCreate}
                      >
                        Add new
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className="row mb-4 align-items-stretch debt-summary-with-status-row">
                  <div className="col-lg-7 mb-4 mb-lg-0 d-flex flex-column">
                    <div className="row flex-shrink-0">
                      <div className="col-6 col-md-4 mb-3 pr-0">
                        <SummaryTile
                          bordered
                          label="Unique ref"
                          value={record.unique_reference}
                        />
                      </div>
                      <div className="col-6 col-md-4 mb-3 pr-0">
                        <SummaryTile
                          bordered
                          label="Mechanism"
                          value={debtMechanismLabelShort(
                            record.collection_mechanism,
                            record.collection_percent,
                          )}
                        />
                      </div>
                      <div className="col-6 col-md-4 mb-3">
                        <SummaryTile
                          bordered
                          label="Opening balance"
                          value={formatMoney(record.opening_balance)}
                          large
                        />
                      </div>
                      <div className="col-6 col-md-4 pr-0">
                        <SummaryTile
                          bordered
                          label="Current balance"
                          value={formatMoney(record.current_balance)}
                          large
                        />
                      </div>
                      <div className="col-6 col-md-4 pr-0">
                        <SummaryTile
                          bordered
                          label="Started at"
                          value={
                            record.start_date
                              ? moment(record.start_date).format("DD MMM YYYY")
                              : "—"
                          }
                        />
                      </div>
                      <div className="col-6 col-md-4">
                        <SummaryTile
                          bordered
                          label="Created by"
                          value={
                            record.created_by_display ||
                            record.created_by_name ||
                            formatDebtUserRef(record.created_by) ||
                            record.created_by_user ||
                            "—"
                          }
                        />
                      </div>
                    </div>
                    <div
                      className="debt-summary-tiles-spacer flex-grow-1 d-none d-lg-block"
                      aria-hidden
                    />
                  </div>
                  <div className="col-lg-5 mb-4 mb-lg-0 d-flex flex-column">
                    <InfoPanel
                      title="Status and rules"
                      className="flex-fill debt-status-rules-panel-flex"
                    >
                      <ul className="small text-muted pl-3 mb-0">
                        <li>
                          <strong className="text-dark">Pending:</strong>{" "}
                          editable and deletable
                        </li>
                        <li>
                          <strong className="text-dark">Ongoing:</strong>{" "}
                          collection in progress
                        </li>
                        <li>
                          <strong className="text-dark">Completed:</strong> full
                          amount collected
                        </li>
                        <li>
                          <strong className="text-dark">Closed:</strong>{" "}
                          prematurely closed with audit trail
                        </li>
                        <li>
                          <strong className="text-dark">Add new</strong> is
                          disabled while a Pending or Ongoing configuration
                          exists.
                        </li>
                      </ul>
                    </InfoPanel>
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-lg-7 mb-4 mb-lg-0">
                    <InfoPanel title="Audit trail">
                      <div className="small">
                        <div className="mb-2">
                          <span className="text-muted">Created on: </span>
                          <span className="text-dark font-weight-bold">
                            {record.created_at
                              ? moment(record.created_at).format(
                                  "DD MMM YYYY, HH:mm",
                                )
                              : "—"}
                          </span>
                        </div>
                        <div className="mb-2">
                          <span className="text-muted">Last updated: </span>
                          <span className="text-dark font-weight-bold">
                            {record.updated_at
                              ? moment(record.updated_at).format(
                                  "DD MMM YYYY, HH:mm",
                                )
                              : "—"}
                          </span>
                        </div>
                        <div className="mb-2">
                          <span className="text-muted">Status owner: </span>
                          <span className="text-dark font-weight-bold">
                            {getDebtStatusOwnerDisplay(record, status)}
                          </span>
                        </div>
                        {status === "closed" ? (
                          <>
                            <div className="mb-2">
                              <span className="text-muted">Closure date: </span>
                              <span className="text-dark font-weight-bold">
                                {record.closure_date
                                  ? moment(record.closure_date).format(
                                      "DD MMM YYYY",
                                    )
                                  : "—"}
                              </span>
                            </div>
                            <div className="mb-2">
                              <span className="text-muted">Closure reason / remark: </span>
                              <span className="text-dark font-weight-bold">
                                {record.closed_reason != null &&
                                String(record.closed_reason).trim() !== ""
                                  ? String(record.closed_reason)
                                  : record.closure_remark || "—"}
                              </span>
                            </div>
                            <div className="mb-0">
                              <span className="text-muted">Closed by: </span>
                              <span className="text-dark font-weight-bold">
                                {record.closed_by_display ||
                                  formatDebtUserRef(record.closed_by) ||
                                  "—"}
                              </span>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </InfoPanel>
                  </div>
                  <div className="col-lg-5">
                    <InfoPanel title="Recommended page actions">
                      <ul className="small text-muted pl-3 mb-0">
                        <li>
                          <strong className="text-dark">Edit</strong> visible
                          only when status = Pending
                        </li>
                        <li>
                          <strong className="text-dark">Delete</strong> visible
                          only when status = Pending
                        </li>
                        <li>
                          <strong className="text-dark">Close account</strong>{" "}
                          opens a confirmation dialog
                        </li>
                        <li>
                          <strong className="text-dark">Status badge</strong> is
                          always visible near page title
                        </li>
                      </ul>
                    </InfoPanel>
                  </div>
                </div>

                {readOnly ? (
                  <div
                    className="d-flex flex-wrap align-items-center pt-2 border-top"
                    style={{ borderColor: CARD_BORDER }}
                  >
                    <span className="text-muted small mb-2 d-inline-block">
                      This configuration is closed and read-only.
                    </span>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      <DebtCollectionSectionDrawer
        open={drawerOpen}
        drawerMode={drawerMode}
        submitting={submitting}
        form={form}
        setForm={setForm}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        onClose={closeDrawer}
        onSubmit={handleSubmitForm}
        dismissFieldErrorIfValid={dismissFieldErrorIfValid}
      />

      <DebtCollectionDeleteModal
        show={deleteModal}
        submitting={submitting}
        onHide={closeDeleteModal}
        onConfirmDelete={handleDelete}
      />

      <DebtCollectionCloseAccountModal
        show={closeModal}
        closeSubmitting={closeSubmitting}
        onHide={closeCloseModal}
        onConfirmClose={handleCloseAccount}
        closeForm={closeForm}
        setCloseForm={setCloseForm}
        closeRemarkError={closeRemarkError}
        setCloseRemarkError={setCloseRemarkError}
        closeAccountClosedByField={closeAccountClosedByField}
      />

      {snack.call ? <SnackBarTool {...snack} /> : null}
    </>
  );
}

/**
 * Franchise details section for debt collection; hidden if the user lacks access.
 * @param {{ franchiseId?: string|number|null }} props - Section props.
 * @param {string|number|null|undefined} props.franchiseId - Current franchise id from the parent page.
 * @returns {JSX.Element|null} Section UI or null when role check fails.
 */
export function DebtCollectionSection({ franchiseId }) {
  const user = useSelector((state) => state.auth.user, shallowEqual);
  const roles = user?.roles;
  const canView = useMemo(() => canAccessDebtCollectionFlow(roles), [roles]);

  if (!canView) return null;

  return <DebtCollectionSectionContent franchiseId={franchiseId} user={user} />;
}
